/**
 * ServicesTab Component
 * Configure service offerings (container sizes, availability)
 */

import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ServicesSchema, type ServicesFormData } from '../../schemas/widgetConfigSchema';
import { useUpdateServices } from '../../hooks/useWidgetConfig';
import { FormSection, FormField } from './shared/FormSection';
import { Button } from '../ui/button';
import { CONTAINER_SIZES, type ContainerSize } from '../../types/widget-config';
import type { WidgetConfiguration } from '../../types/widget-config';

export interface ServicesTabProps {
  config: WidgetConfiguration;
}

/**
 * ServicesTab allows providers to manage service offerings
 */
export function ServicesTab({ config }: ServicesTabProps) {
  const updateServices = useUpdateServices();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<ServicesFormData>({
    resolver: zodResolver(ServicesSchema),
    defaultValues: {
      services: config.services.length > 0
        ? config.services
        : [{ containerSize: '20Y', description: '', available: true }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'services',
  });

  const onSubmit = (data: ServicesFormData) => {
    updateServices.mutate(data);
  };

  const handleAddService = () => {
    // Find a container size not already in the list
    const usedSizes = fields.map(f => f.containerSize);
    const availableSize = CONTAINER_SIZES.find(size => !usedSizes.includes(size)) || '20Y';

    append({
      containerSize: availableSize as ContainerSize,
      description: '',
      available: true,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <FormSection
        title="Container Services"
        description="Manage the container sizes and services you offer"
      >
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="p-4 border border-gray-200 rounded-lg space-y-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Container Size */}
                  <FormField
                    label="Container Size"
                    required
                    error={errors.services?.[index]?.containerSize?.message}
                    htmlFor={`services.${index}.containerSize`}
                  >
                    <select
                      {...register(`services.${index}.containerSize`)}
                      id={`services.${index}.containerSize`}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {CONTAINER_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size} Dumpster
                        </option>
                      ))}
                    </select>
                  </FormField>

                  {/* Capacity */}
                  <FormField
                    label="Capacity (Optional)"
                    description="Max containers available"
                    error={errors.services?.[index]?.capacity?.message}
                    htmlFor={`services.${index}.capacity`}
                  >
                    <input
                      {...register(`services.${index}.capacity`, {
                        valueAsNumber: true,
                      })}
                      id={`services.${index}.capacity`}
                      type="number"
                      min="0"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="10"
                    />
                  </FormField>
                </div>

                {/* Remove Button */}
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    aria-label="Remove service"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Description */}
              <FormField
                label="Description"
                required
                error={errors.services?.[index]?.description?.message}
                htmlFor={`services.${index}.description`}
              >
                <input
                  {...register(`services.${index}.description`)}
                  id={`services.${index}.description`}
                  type="text"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Perfect for small cleanouts and renovations"
                />
              </FormField>

              {/* Available Toggle */}
              <div className="flex items-center gap-2">
                <input
                  {...register(`services.${index}.available`)}
                  id={`services.${index}.available`}
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label
                  htmlFor={`services.${index}.available`}
                  className="text-sm font-medium text-gray-900"
                >
                  Available for booking
                </label>
              </div>
            </div>
          ))}

          {/* Add Service Button */}
          {fields.length < CONTAINER_SIZES.length && (
            <Button
              type="button"
              variant="outline"
              onClick={handleAddService}
              className="w-full"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Container Size
            </Button>
          )}
        </div>

        {errors.services?.root && (
          <p className="text-sm text-red-600" role="alert">
            {errors.services.root.message}
          </p>
        )}
      </FormSection>

      {/* Save Button */}
      <div className="flex justify-end gap-3 pt-6 border-t">
        <Button
          type="submit"
          disabled={!isDirty || updateServices.isPending}
          className="min-w-[120px]"
        >
          {updateServices.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Unsaved Changes Warning */}
      {isDirty && (
        <p className="text-sm text-amber-600" role="status">
          You have unsaved changes
        </p>
      )}
    </form>
  );
}
